<script setup lang="ts">
/* eslint-disable */
import { logger, onMounted, useFetch } from '#imports';
import { useGlobalStore } from '@/stores/globalStore';
import { useYouTubePlayerStore } from '@/stores/youtubePlayerStore';
import type { NavigationNode, ShellData } from 'data-types-config';
import { computed, provide, watch } from 'vue';

defineOptions({ name: 'AppShellLayout' });

const isDev = process.dev;

const globalStore = useGlobalStore();
const youtubeStore = useYouTubePlayerStore();

onMounted(async () => {
  try {
    await youtubeStore.ensureYoutubeApiReady();
  } catch (err) {
    logger.error('[YouTube] Init error:', err);
  }
});

/* Fetch navigation shell data — no await (don't block mount) */
const {
  data: shellData,
  pending,
  error,
  refresh: refreshShell,
} = useFetch<ShellData>('/api/shell/navigation', {
  key: 'app-shell-navigation',
  default: () => ({}) as ShellData,
  server: true,
  lazy: true,
});

/* Log only when the error actually changes */
watch(error, (e) => {
  if (e) logger.error('Error fetching shell navigation data:', e);
});

const mainNavLinks = computed<NavigationNode[]>(() => [
  ...(shellData.value?.headerBeliefsNavigation?.children ?? []),
  ...(shellData.value?.headerQandaNavigation?.children ?? []),
  ...(shellData.value?.headerCampaignsNavigation?.children ?? []),
]);

const mainNavLinksMobile = computed<NavigationNode[]>(() => [
  ...(shellData.value?.mobileDiscoverNavigation?.children ?? []),
  ...(shellData.value?.mobileQandaNavigation?.children ?? []),
  ...(shellData.value?.mobileMoreNavigation?.children ?? []),
]);

const utilityNavigation = computed<NavigationNode[]>(
  () => shellData.value?.secondNavFooterNavigation?.children ?? [],
);

/* UI state */

const isLoadingPage = computed(() => {
  // Only show loading state if we are on the client and something is actually pending
  if (process.server) return false;
  return (
    globalStore.isPageLoading ||
    (pending.value && !shellData.value?.headerBeliefsNavigation)
  );
});

/* Provide data to child components */
provide('shellData', shellData);

// Provide navigation - skip root wrapper, get children of first item (Beliefs -> its children: Allah, Prophets, etc.)
provide(
  'beliefsLinks',
  computed(
    () =>
      shellData.value?.headerBeliefsNavigation?.children?.[0]?.children ?? [],
  ),
);

// Provide legal links separately as requested
provide(
  'legalLinks',
  computed(() => shellData.value?.footerLegalNavigation?.children ?? []),
);

// Provide footer navigation
provide(
  'footerNavigation',
  computed(() => shellData.value?.footerNavigation),
);
// Provide utility navigation (renamed from secondNavLinks)
provide('utilityNavigation', utilityNavigation);

/* Expose utilities (if a parent needs to call it) */
function refreshNavigation() {
  return refreshShell();
}

defineExpose({ refreshNavigation });
</script>

<template lang="pug">
EcreativAtomsSpinner(
  v-if="isLoadingPage"
  isFullPage
  hasBackground
)
  LogoLoader

NuxtErrorBoundary
  template(#error="{ error: boundaryError }")
    .error-boundary
      h1 Error Loading the Page
      p {{ boundaryError }}

  Shell
    template(#header)
      EcreativOrganismsPresentingLogoAndCallToNavigateMainDirectory2(
        :mainNavLinks="mainNavLinks"
        :mainNavLinksMobile="mainNavLinksMobile"
        :secondNavLinks="utilityNavigation"
      )

    template(#footer)
      EcreativOrganismsFooterNavigationAndLegal(
        :navigation="shellData?.footerNavigation"
        :legalNavigation="shellData?.footerLegalNavigation"
      )
</template>
